import { useMemo } from "react";
import { useForm } from "react-hook-form";
import type { FormField } from "@colab/shared";
import { FIELD_RENDERERS } from "@/features/portal/forms";

// Inline render of a single field inside the builder canvas. Reuses the
// partner-side FIELD_RENDERERS registry so the preview's chrome (label,
// help text, required marker, error slot) matches what a real partner
// sees at /portal/forms/:slug.
//
// Important divergence from legacy /components/admin/forms/FieldPreview.tsx:
// the legacy wrapped <FormRenderer disabled>, but the SPA portal abandoned
// FormRenderer in favor of consuming FIELD_RENDERERS directly (see
// apps/web/src/features/portal/forms/field-renderers/field-registry.tsx).
// Phase 7 sub-bucket 11 keeps the SPA on the same registry: render the
// kind's component inside a synthetic single-field useForm context with
// `disabled` propagated. section_heading short-circuits to the legacy
// dashed-border look (mounting the renderer's <h2> inside a "field preview"
// frame would create a double-heading effect).

const SECTION_HEADING_PREVIEW_CLASSES =
  "rounded-md border border-dashed border-border/60 bg-muted/30 px-3 py-2 text-sm font-medium";

export function FieldPreview({ field }: { field: FormField }) {
  if (field.kind === "section_heading") {
    return (
      <div className={SECTION_HEADING_PREVIEW_CLASSES}>
        {field.label || (
          <em className="text-muted-foreground">Section heading</em>
        )}
      </div>
    );
  }

  return <FieldPreviewInner field={field} />;
}

function FieldPreviewInner({ field }: { field: FormField }) {
  // Re-instantiate the useForm context per field id so kind switches don't
  // carry stale defaults from a previous render.
  const defaults = useMemo(
    () => ({ [field.name]: defaultValueForKind(field) }),
    [field],
  );
  const form = useForm<Record<string, unknown>>({
    defaultValues: defaults,
    mode: "onBlur",
  });

  const Renderer = FIELD_RENDERERS[field.kind];
  if (!Renderer) return null;

  return (
    <div
      // pointer-events-none — clicks fall through to the FieldBlock select
      // handler. select-none — drag interactions stay clean.
      className="pointer-events-none select-none rounded-md border border-dashed border-border/60 bg-muted/20 px-3 py-3"
    >
      <Renderer field={field} control={form.control} disabled />
    </div>
  );
}

function defaultValueForKind(field: FormField): unknown {
  switch (field.kind) {
    case "text":
    case "long_text":
    case "email":
    case "date":
    case "partner_lookup":
      return "";
    case "number":
      return null;
    case "select":
      return field.multiple ? [] : "";
    case "checkbox":
      return false;
    case "file":
      return null;
    case "section_heading":
      return undefined;
  }
}
